import { isEmptyValue } from "../../../utils/formValueUtils";
import { parse, isValid as isValidDate } from "date-fns";
import { parseApiFormat } from "./dateValidatorUtils";
import { getAdjustedMaxLength } from "../../../utils/fieldValidation";

/**
 * Frontend Field Validator
 *
 * Validates individual field values against their schema (pattern, format_hint, min/max values)
 * This provides immediate client-side validation without waiting for backend response
 */

/**
 * Validate a single field against its schema
 * Returns a FieldError if validation fails, null if passes
 */
export var validateField = function validateField(fieldSchema, value) {
  var _metadata$pattern;
  var fieldName = fieldSchema.field_name;
  var metadata = fieldSchema.metadata;

  // Skip validation for empty values (required validation is handled separately)
  if (isEmptyValue(value)) {
    return null;
  }

  // Convert value to string for pattern matching
  var stringValue = String(value).trim();

  // 1. Pattern validation
  if (metadata.pattern) {
    try {
      var regex = new RegExp(metadata.pattern);
      if (!regex.test(stringValue)) {
        var errorMessage = metadata.format_hint ? "Invalid format. ".concat(metadata.format_hint) : "Invalid format for ".concat(fieldSchema.label);
        return {
          field: fieldName,
          error: errorMessage,
          type: "validation"
        };
      }
    } catch (e) {
      // Invalid regex pattern - skip validation
      console.warn("Invalid regex pattern for field ".concat(fieldName, ":"), metadata.pattern);
    }
  }

  // 2. Numeric range validation (min_value, max_value)
  if (metadata.min_value !== null || metadata.max_value !== null) {
    var numericValue = Number(value);

    // Only validate if it's a valid number
    if (!isNaN(numericValue)) {
      if (metadata.min_value !== null && numericValue < metadata.min_value) {
        return {
          field: fieldName,
          error: "Value must be at least ".concat(metadata.min_value),
          type: "validation"
        };
      }
      if (metadata.max_value !== null && numericValue > metadata.max_value) {
        return {
          field: fieldName,
          error: "Value must be at most ".concat(metadata.max_value),
          type: "validation"
        };
      }
    }
  }

  // 3. Max length validation
  // Use adjusted max length to account for separators in formatted patterns
  var adjustedMaxLength = getAdjustedMaxLength(metadata.max_length, (_metadata$pattern = metadata.pattern) !== null && _metadata$pattern !== void 0 ? _metadata$pattern : undefined);
  if (adjustedMaxLength && stringValue.length > adjustedMaxLength) {
    return {
      field: fieldName,
      error: "Maximum length is ".concat(metadata.max_length, " characters"),
      type: "validation"
    };
  }

  // 4. Future date validation for date fields
  var dateFormat = parseApiFormat(metadata.format_hint) || "MM/dd/yyyy";
  var futureDateError = validateDateFieldForFutureDate(fieldName, stringValue, dateFormat);
  if (futureDateError) {
    return {
      field: fieldName,
      error: futureDateError,
      type: "validation"
    };
  }
  return null;
};

/**
 * Date fields that should not allow future dates
 * Add any date field names here that should be validated against future dates
 */
var DATE_FIELDS_NO_FUTURE = {
  Date_Of_Birth: "Date of birth",
  Date_Of_Death: "Date of death",
  Exam_Date: "Examination date",
  Originally_Scheduled_Exam_Date: "Originally scheduled exam date",
  COVID_Diagnosis_Date: "COVID diagnosis date",
  COVID_Vaccine_Date: "COVID vaccine date",
  Date_Of_Follow_Up: "Date of Follow-up"
  // Add more date fields here as needed
  // Field_Name: "Field Label for error message"
};

/**
 * Validate date fields to ensure they are not future dates
 * Returns error message if validation fails, null if passes
 */
export var validateDateFieldForFutureDate = function validateDateFieldForFutureDate(fieldName, dateString, dateFormat) {
  // Check if this field should be validated for future dates
  var fieldLabel = DATE_FIELDS_NO_FUTURE[fieldName];
  if (!fieldLabel) {
    return null;
  }

  // Skip validation if date string is empty
  if (!dateString || dateString.trim() === "") {
    return null;
  }

  // Parse and validate the date
  var parsedDate = parse(String(dateString).trim(), dateFormat, new Date());
  if (!isValidDate(parsedDate)) {
    return null; // Invalid date format will be caught by other validators
  }

  // Check if date is in the future
  var today = new Date();
  today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison

  if (parsedDate > today) {
    return "".concat(fieldLabel, " cannot be a future date.");
  }
  return null;
};

/**
 * Normalize multi-select field values to handle mutual exclusivity
 * Certain values like "Unknown" and "Not reported" are mutually exclusive with other options
 *
 * @param fieldName - The field name to check
 * @param currentValues - Currently selected values
 * @param newValue - The newly toggled value
 * @returns Normalized array of values respecting mutual exclusivity rules
 */
export var normalizePatientRaceSelection = function normalizePatientRaceSelection(fieldName, currentValues, newValue) {
  // Define exclusive values for each field
  var exclusiveValues = [];
  if (fieldName === "Patient_Race") {
    exclusiveValues = ["9", "10"]; // "Not reported" and "Unknown"
  } else if (fieldName === "Health_Insurance") {
    exclusiveValues = ["5"]; // "Unknown"
  } else if (fieldName === "What_Were_The_Other_Findings") {
    exclusiveValues = ["7", "9"]; // No significant abnormalities and Unknown
  } else if (fieldName === "Occupational_Exposures") {
    exclusiveValues = ["9", "10"]; // None and Unknown
  } else if (fieldName === "History_Of_Cancers") {
    exclusiveValues = ["88", "99"]; // No history and Unknown
  } else {
    // No exclusivity rules for this field
    return currentValues;
  }
  var isExclusiveValue = exclusiveValues.includes(newValue);
  var hasExclusiveValue = currentValues.some(function (v) {
    return exclusiveValues.includes(v);
  });

  // If toggling an exclusive value
  if (isExclusiveValue) {
    // Remove all non-exclusive values, keep only the new exclusive value
    return [newValue];
  }

  // If toggling a non-exclusive value and there are exclusive values selected
  if (!isExclusiveValue && hasExclusiveValue) {
    // Remove all exclusive values, keep only non-exclusive values including the new one
    return currentValues.filter(function (v) {
      return !exclusiveValues.includes(v);
    });
  }

  // Normal case - no conflicts
  return currentValues;
};

/**
 * Validate all fields in form data
 * Returns array of validation errors
 */
export var validateAllFields = function validateAllFields(formData, fieldSchemas) {
  var errors = [];
  fieldSchemas.forEach(function (fieldSchema) {
    var value = formData[fieldSchema.field_name];
    var error = validateField(fieldSchema, value);
    if (error) {
      errors.push(error);
    }
  });
  return errors;
};